import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { REDIRECT_BASE } from "@/lib/api";
import { AlertTriangle, ScanLine } from "lucide-react";

/**
 * Public /r/:slug redirect page.
 * Immediately hands off to the backend which records the scan and 302-redirects
 * to the customer's Google review URL. This adds one tiny hop but keeps the
 * public link on the branded domain.
 */
export default function RedirectPage() {
  const { slug, code } = useParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    const target = code
      ? `${REDIRECT_BASE.replace(/\/r$/, "")}/t/${encodeURIComponent(code)}`
      : `${REDIRECT_BASE}/${encodeURIComponent(slug)}`;
    // Pre-flight to differentiate 404 / 410 from network errors so we can show a clean page.
    fetch(target, { method: "HEAD", redirect: "manual" })
      .then((res) => {
        if (res.type === "opaqueredirect" || res.status === 0 || (res.status >= 200 && res.status < 400)) {
          window.location.replace(target);
        } else if (res.status === 404) {
          setError({ code: 404, message: "Dieser Bewertungslink existiert nicht." });
        } else if (res.status === 410) {
          setError({ code: 410, message: "Dieser Bewertungslink ist deaktiviert." });
        } else {
          window.location.replace(target);
        }
      })
      .catch(() => window.location.replace(target));
  }, [slug, code]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center px-4">
        <div className="card-surface p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto" />
          <h1 className="font-heading text-2xl font-semibold mt-3">Nicht verfügbar</h1>
          <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
          <Link
            to="/"
            className="mt-6 inline-block text-xs text-muted-foreground hover:text-foreground"
          >
            Zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary grid place-items-center">
          <ScanLine className="w-6 h-6 animate-pulse" />
        </div>
        <p className="text-sm">Du wirst zu Google weitergeleitet…</p>
      </div>
    </div>
  );
}
